import React, { useState } from "react";
import List from "../components/inputs/List";
import PROJECT_CONFIG from "../config/projectConfig";

const Process = () => {
  const [activeTab, setActiveTab] = useState("overview");

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "process", label: "Process" },
    { id: "panel-formation", label: "Panel Formation" },
    { id: "review-process", label: "Review Process" },
    { id: "decision", label: "Decision" },
    { id: "timeline", label: "Timeline" },
  ];

  const handleTabKeyDown = (event, index) => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft" && event.key !== "Home" && event.key !== "End") {
      return;
    }
    event.preventDefault();
    let nextIndex = index;
    if (event.key === "ArrowRight") {
      nextIndex = (index + 1) % tabs.length;
    } else if (event.key === "ArrowLeft") {
      nextIndex = (index - 1 + tabs.length) % tabs.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = tabs.length - 1;
    }
    setActiveTab(tabs[nextIndex].id);
    document.getElementById(`process-tab-${tabs[nextIndex].id}`)?.focus();
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Overview</h2>
            <p className="text-gray-700 leading-relaxed">
              The {PROJECT_CONFIG.fullName} ({PROJECT_CONFIG.name}) provides an
              independent review of conflicting scientific and technical data
              submitted by communities challenging FEMA's flood hazard
              information. The {PROJECT_CONFIG.name} process ensures fair,
              unbiased, and scientifically sound decisions regarding flood
              hazard determinations.
            </p>

            <div className="bg-blue-50 border-l-4 border-custom-blue p-4">
              <h4 className="font-semibold text-gray-900 mb-2">
                Key Principles
              </h4>
              <List
                items={[
                  "Independence and impartiality",
                  "Scientific and technical expertise",
                  "Transparent decision-making process",
                  "Timely resolution of conflicts",
                ]}
                spacing="tight"
              />
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                When to Use the {PROJECT_CONFIG.name}
              </h4>
              <p className="text-gray-700 mb-4">
                The {PROJECT_CONFIG.name} process is available when:
              </p>
              <List
                items={[
                  "Community consultation with FEMA has not resolved the conflict",
                  "The community has scientific or technical data that conflicts with FEMA's proposed flood hazard determinations",
                  "The dispute involves Base Flood Elevations, base flood depths, or Special Flood Hazard Area boundaries",
                  "The community is willing to submit their data for independent review",
                ]}
              />
            </div>
          </div>
        );

      case "process":
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {PROJECT_CONFIG.name} Process
            </h2>

            <div className="space-y-8">
              {/* Step 1 */}
              <div className="flex">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-custom-blue text-white flex items-center justify-center font-bold">
                  1
                </div>
                <div className="ml-4 flex-1">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Community Consultation
                  </h4>
                  <p className="text-gray-700">
                    Before initiating an {PROJECT_CONFIG.name}, communities must
                    first attempt to resolve conflicts through FEMA's
                    consultation process. This involves direct communication
                    with FEMA to discuss the technical concerns and attempt to
                    reach a mutually acceptable resolution.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-custom-blue text-white flex items-center justify-center font-bold">
                  2
                </div>
                <div className="ml-4 flex-1">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    {PROJECT_CONFIG.name} Request
                  </h4>
                  <p className="text-gray-700">
                    If consultation fails to resolve the conflict, the community
                    may request an {PROJECT_CONFIG.name}. The request must
                    include:
                  </p>
                  <List
                    items={[
                      "Documentation of the consultation process",
                      "Specific technical or scientific data in conflict",
                      "Supporting technical documentation",
                      "Proposed alternative flood hazard determinations",
                    ]}
                    withMargin
                    className="mt-2"
                  />
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-custom-blue text-white flex items-center justify-center font-bold">
                  3
                </div>
                <div className="ml-4 flex-1">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Panel Formation
                  </h4>
                  <p className="text-gray-700">
                    NIBS assembles a panel of independent experts with relevant
                    technical expertise. Panel members are selected based on
                    their qualifications and absence of conflicts of interest.
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-custom-blue text-white flex items-center justify-center font-bold">
                  4
                </div>
                <div className="ml-4 flex-1">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Technical Review
                  </h4>
                  <p className="text-gray-700">
                    The panel reviews all submitted technical data, may request
                    additional information, and conducts a thorough scientific
                    analysis of the conflicting determinations.
                  </p>
                </div>
              </div>

              {/* Step 5 */}
              <div className="flex">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-custom-blue text-white flex items-center justify-center font-bold">
                  5
                </div>
                <div className="ml-4 flex-1">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Panel Decision
                  </h4>
                  <p className="text-gray-700">
                    The panel renders a written decision that either accepts or
                    denies the community's alternative data. The decision
                    includes detailed technical justification and serves as a
                    recommendation to the FEMA Administrator.
                  </p>
                </div>
              </div>

              {/* Step 6 */}
              <div className="flex">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-custom-blue text-white flex items-center justify-center font-bold">
                  6
                </div>
                <div className="ml-4 flex-1">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Implementation
                  </h4>
                  <p className="text-gray-700">
                    FEMA reviews the panel's recommendation and makes a final
                    determination. If accepted, FEMA incorporates the
                    alternative data into the flood hazard determinations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case "panel-formation":
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Panel Formation
            </h2>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Panel Composition
              </h4>
              <p className="text-gray-700 mb-4">
                Each {PROJECT_CONFIG.name} typically consists of three to five
                members with expertise relevant to the specific technical issues
                in dispute. The panel composition ensures comprehensive coverage
                of all technical aspects.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Member Qualifications
              </h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <List
                  items={[
                    "Minimum 10 years of relevant professional experience",
                    "Recognized expertise in flood hazard analysis and mapping",
                    "Professional licenses or certifications as appropriate",
                    "No conflicts of interest with the parties involved",
                    "Ability to provide impartial technical review",
                  ]}
                />
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Areas of Expertise
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-2">
                    Hydraulic Engineering
                  </h5>
                  <List
                    items={[
                      "Open channel hydraulics",
                      "Floodway analysis",
                      "Bridge and culvert hydraulics",
                      "Dam break analysis",
                    ]}
                    size="sm"
                    variant="light"
                    spacing="tight"
                  />
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-2">
                    Hydrologic Engineering
                  </h5>
                  <List
                    items={[
                      "Rainfall-runoff modeling",
                      "Statistical analysis",
                      "Watershed modeling",
                      "Frequency analysis",
                    ]}
                    size="sm"
                    variant="light"
                    spacing="tight"
                  />
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-2">
                    Coastal Engineering
                  </h5>
                  <List
                    items={[
                      "Storm surge modeling",
                      "Wave analysis",
                      "Coastal erosion",
                      "Sea level rise impacts",
                    ]}
                    size="sm"
                    variant="light"
                    spacing="tight"
                  />
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-2">
                    Geotechnical Engineering
                  </h5>
                  <List
                    items={[
                      "Levee analysis",
                      "Dam safety",
                      "Alluvial fan flooding",
                      "Sediment transport",
                    ]}
                    size="sm"
                    variant="light"
                    spacing="tight"
                  />
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Selection Process
              </h4>
              <p className="text-gray-700">
                Panel members are selected from NIBS's cadre of qualified
                experts through a rigorous process that considers:
              </p>
              <List
                ordered
                items={[
                  "Technical expertise relevant to the specific dispute",
                  "Geographic knowledge of the area (when applicable)",
                  "Availability to meet timeline requirements",
                  "Absence of conflicts of interest",
                  `Previous ${PROJECT_CONFIG.name} experience and performance`,
                ]}
                className="mt-3"
              />
            </div>
          </div>
        );

      case "review-process":
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Review Process</h2>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Initial Review
              </h4>
              <p className="text-gray-700 mb-4">
                Upon panel formation, members conduct an initial review of all
                submitted materials:
              </p>
              <List
                items={[
                  "FEMA's technical data and proposed determinations",
                  "Community's alternative technical data",
                  "Supporting documentation from both parties",
                  "Relevant historical flood data",
                  "Previous studies and reports",
                ]}
              />
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Technical Analysis
              </h4>
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-gray-700 mb-3">
                  The panel conducts a comprehensive technical analysis that may
                  include:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium text-gray-900 mb-2">
                      Data Validation
                    </h5>
                    <List
                      items={[
                        "Verify data accuracy and completeness",
                        "Check calculation methodologies",
                        "Validate model inputs and assumptions",
                        "Review quality assurance procedures",
                      ]}
                      size="sm"
                      variant="light"
                      spacing="tight"
                    />
                  </div>
                  <div>
                    <h5 className="font-medium text-gray-900 mb-2">
                      Comparative Analysis
                    </h5>
                    <List
                      items={[
                        "Compare methodologies used",
                        "Evaluate technical merit",
                        "Assess compliance with standards",
                        "Identify key differences",
                      ]}
                      size="sm"
                      variant="light"
                      spacing="tight"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Information Requests
              </h4>
              <p className="text-gray-700 mb-4">
                During the review, the panel may request additional information
                from either party:
              </p>
              <div className="border-l-4 border-custom-light-blue pl-4">
                <List
                  items={[
                    "Clarification of technical methodologies",
                    "Additional supporting data or documentation",
                    "Model files and computational details",
                    "Field survey data or observations",
                    "Responses to specific technical questions",
                  ]}
                />
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Deliberation Process
              </h4>
              <p className="text-gray-700 mb-4">
                Panel members deliberate to reach a consensus decision:
              </p>
              <List
                ordered
                items={[
                  "Individual review and analysis by each panel member",
                  "Group discussions to share findings and perspectives",
                  "Identification of key technical issues",
                  "Evaluation of evidence supporting each position",
                  "Development of consensus on technical merit",
                  "Drafting of decision with technical justification",
                ]}
              />
            </div>
          </div>
        );

      case "decision":
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Panel Decision</h2>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Decision Types
              </h4>
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h5 className="font-medium text-green-900 mb-2">
                    Accept Alternative Data
                  </h5>
                  <p className="text-gray-700">
                    The panel finds that the community's alternative data is
                    technically superior and scientifically correct. FEMA should
                    use this data for flood hazard determinations.
                  </p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h5 className="font-medium text-red-900 mb-2">
                    Deny Alternative Data
                  </h5>
                  <p className="text-gray-700">
                    The panel finds that FEMA's data is technically superior and
                    scientifically correct. The community's alternative data
                    does not warrant changes to the proposed determinations.
                  </p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h5 className="font-medium text-yellow-900 mb-2">
                    Partial Acceptance
                  </h5>
                  <p className="text-gray-700">
                    The panel may accept some portions of the alternative data
                    while denying others, or recommend a compromise solution
                    based on technical merit.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Decision Documentation
              </h4>
              <p className="text-gray-700 mb-4">
                The panel's written decision includes:
              </p>
              <div className="bg-gray-50 rounded-lg p-4">
                <List
                  items={[
                    "<strong>Executive Summary:</strong> Brief overview of the decision and key findings",
                    "<strong>Technical Background:</strong> Description of the dispute and data reviewed",
                    "<strong>Analysis:</strong> Detailed technical evaluation of conflicting data",
                    "<strong>Findings:</strong> Specific technical conclusions on each issue",
                    "<strong>Recommendation:</strong> Clear statement of the panel's decision",
                    "<strong>Justification:</strong> Technical rationale supporting the decision",
                    "<strong>Appendices:</strong> Supporting technical documentation as needed",
                  ]}
                />
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Decision Criteria
              </h4>
              <p className="text-gray-700 mb-4">
                The panel bases its decision on:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-2">
                    Technical Merit
                  </h5>
                  <List
                    items={[
                      "Scientific accuracy",
                      "Appropriate methodologies",
                      "Data quality and reliability",
                      "Computational correctness",
                    ]}
                    size="sm"
                    variant="light"
                    spacing="tight"
                  />
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-2">Compliance</h5>
                  <List
                    items={[
                            "FEMA guidelines and standards",
                      "Industry best practices",
                      "Regulatory requirements",
                      "Professional standards",
                    ]}
                    size="sm"
                    variant="light"
                    spacing="tight"
                  />
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Implementation
              </h4>
              <p className="text-gray-700">Following the panel's decision:</p>
              <List
                ordered
                items={[
                  "FEMA reviews the panel's recommendation",
                  "FEMA Administrator makes final determination",
                  "Parties are notified of the final decision",
                  "Accepted changes are incorporated into flood maps",
                  "Decision becomes part of the administrative record",
                ]}
                className="mt-3"
              />
            </div>
          </div>
        );

      case "timeline":
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Timeline</h2>

            <div>
              <h4 className="font-semibold text-gray-900 mb-4">
                Typical {PROJECT_CONFIG.name} Timeline
              </h4>
              <div className="relative">
                <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-300"></div>

                <div className="space-y-8">
                  {/* Week 0 */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-16 h-16 rounded-full bg-custom-blue text-white flex flex-col items-center justify-center text-xs font-bold">
                      <span>Week</span>
                      <span>0</span>
                    </div>
                    <div className="ml-6 flex-1">
                      <h5 className="font-semibold text-gray-900">
                        {PROJECT_CONFIG.name} Request Submitted
                      </h5>
                      <p className="text-gray-700 text-sm mt-1">
                        Community submits formal request with all required
                        documentation
                      </p>
                    </div>
                  </div>

                  {/* Week 1-2 */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-16 h-16 rounded-full bg-custom-blue text-white flex flex-col items-center justify-center text-xs font-bold">
                      <span>Week</span>
                      <span>1-2</span>
                    </div>
                    <div className="ml-6 flex-1">
                      <h5 className="font-semibold text-gray-900">
                        Panel Formation
                      </h5>
                      <p className="text-gray-700 text-sm mt-1">
                        NIBS identifies and recruits qualified panel members
                      </p>
                    </div>
                  </div>

                  {/* Week 3-4 */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-16 h-16 rounded-full bg-custom-blue text-white flex flex-col items-center justify-center text-xs font-bold">
                      <span>Week</span>
                      <span>3-4</span>
                    </div>
                    <div className="ml-6 flex-1">
                      <h5 className="font-semibold text-gray-900">
                        Initial Review
                      </h5>
                      <p className="text-gray-700 text-sm mt-1">
                        Panel members review submitted materials and identify
                        information needs
                      </p>
                    </div>
                  </div>

                  {/* Week 5-8 */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-16 h-16 rounded-full bg-custom-blue text-white flex flex-col items-center justify-center text-xs font-bold">
                      <span>Week</span>
                      <span>5-8</span>
                    </div>
                    <div className="ml-6 flex-1">
                      <h5 className="font-semibold text-gray-900">
                        Technical Analysis
                      </h5>
                      <p className="text-gray-700 text-sm mt-1">
                        Detailed technical review, information requests, and
                        analysis
                      </p>
                    </div>
                  </div>

                  {/* Week 9-10 */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-16 h-16 rounded-full bg-custom-blue text-white flex flex-col items-center justify-center text-xs font-bold">
                      <span>Week</span>
                      <span>9-10</span>
                    </div>
                    <div className="ml-6 flex-1">
                      <h5 className="font-semibold text-gray-900">
                        Panel Deliberation
                      </h5>
                      <p className="text-gray-700 text-sm mt-1">
                        Panel members discuss findings and reach consensus
                      </p>
                    </div>
                  </div>

                  {/* Week 11-12 */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-16 h-16 rounded-full bg-custom-blue text-white flex flex-col items-center justify-center text-xs font-bold">
                      <span>Week</span>
                      <span>11-12</span>
                    </div>
                    <div className="ml-6 flex-1">
                      <h5 className="font-semibold text-gray-900">
                        Decision Documentation
                      </h5>
                      <p className="text-gray-700 text-sm mt-1">
                        Panel prepares written decision with technical
                        justification
                      </p>
                    </div>
                  </div>

                  {/* Week 13 */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-16 h-16 rounded-full bg-emerald-800 text-white flex flex-col items-center justify-center text-xs font-bold">
                      <span>Week</span>
                      <span>13</span>
                    </div>
                    <div className="ml-6 flex-1">
                      <h5 className="font-semibold text-gray-900">
                        Decision Delivered
                      </h5>
                      <p className="text-gray-700 text-sm mt-1">
                        Final decision submitted to FEMA and community
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-900 mb-2">
                Important Notes
              </h4>
              <List
                items={[
                  "Timeline may vary based on complexity of technical issues",
                  "Additional time may be needed for information requests",
                  "Expedited review available for time-critical situations",
                  "Extensions may be granted with justification",
                ]}
                size="sm"
              />
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Factors Affecting Timeline
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-2">
                    May Extend Timeline
                  </h5>
                  <List
                    items={[
                      "Complex technical issues",
                      "Multiple areas of dispute",
                      "Extensive data sets",
                      "Need for site visits",
                      "Multiple information requests",
                    ]}
                    size="sm"
                    variant="light"
                    spacing="tight"
                  />
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-2">
                    May Shorten Timeline
                  </h5>
                  <List
                    items={[
                      "Limited scope of dispute",
                      "Clear technical issues",
                      "Complete documentation",
                      "Responsive parties",
                      "Prior panel experience",
                    ]}
                    size="sm"
                    variant="light"
                    spacing="tight"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">
          {PROJECT_CONFIG.name} Process
        </h1>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <div
              className="flex flex-wrap -mb-px"
              role="tablist"
              aria-label={`${PROJECT_CONFIG.name} process sections`}
            >
              {tabs.map((tab, index) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  id={`process-tab-${tab.id}`}
                  aria-selected={activeTab === tab.id}
                  aria-controls={`process-panel-${tab.id}`}
                  tabIndex={activeTab === tab.id ? 0 : -1}
                  onClick={() => setActiveTab(tab.id)}
                  onKeyDown={(e) => handleTabKeyDown(e, index)}
                  className={`py-3 px-6 border-b-2 font-medium text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 ${
                    activeTab === tab.id
                      ? "border-custom-blue text-custom-blue"
                      : "border-transparent text-gray-700 hover:text-gray-900 hover:border-gray-300"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div
          id={`process-panel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`process-tab-${activeTab}`}
          className="bg-white rounded-lg shadow-sm p-8"
        >
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default Process;
